import { Injectable } from '@angular/core';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, finalize, take } from 'rxjs/operators';
import { TEmployeeListInfoActionType } from './employee-list.models';
import * as EmployeeListActions from './employee-list.actions';
import { from, of } from 'rxjs';
import { EmployeeListFacade } from './employee-list.facade';

@Injectable({ providedIn: 'root' })
export class EmployeeListEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private employeeListingFacade: EmployeeListFacade,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  getEmployeeList$ = createEffect(() =>
    this.actions$.pipe(
      ofType<EmployeeListActions.GetEmployeeList>(TEmployeeListInfoActionType.GetEmployeeList),
      switchMap((action) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          switchMap((loaderEl) =>
            this.plani8DataService.fetchEmployeeListData(action.payload).pipe(
              finalize(() => this.appNotificationUtilClass.dismissLoadingV2(loaderEl)),
              map((res) => new EmployeeListActions.GetEmployeeListSuccess(res)),
              catchError(() => of(new EmployeeListActions.GetEmployeeListFailure())),
            ),
          ),
        ),
      ),
    ),
  );

  updateEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TEmployeeListInfoActionType.UpdateEmployee),
      switchMap((action: EmployeeListActions.UpdateEmployees) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateEmployeeData(action.updatePayload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => new EmployeeListActions.GetEmployeeList(action.fetchPayload)),
          catchError(() => {
            return of(new EmployeeListActions.UpdateEmployeesFailure());
          }),
        ),
      ),
    ),
  );

  getTeamList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TEmployeeListInfoActionType.GetTeamList),
      switchMap((action: EmployeeListActions.GetTeamList) =>
        this.plani8DataService.fetchTeamList().pipe(
          map((res) => new EmployeeListActions.GetTeamListSuccess(res)),
          catchError(() => {
            return of(new EmployeeListActions.GetTeamListFailure());
          }),
        ),
      ),
    ),
  );

  updateTeams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TEmployeeListInfoActionType.UpdateTeams),
      switchMap((action: EmployeeListActions.UpdateTeams) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.updateTeams(action.updatePayload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.employeeListingFacade.fetchEmployeeListInfo(action.fetchEmployeePayload);
            return new EmployeeListActions.GetTeamList();
          }),
          catchError(() => {
            return of(new EmployeeListActions.UpdateTeamsFailure());
          }),
        ),
      ),
    ),
  );

  deleteTeams$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TEmployeeListInfoActionType.DeleteTeams),
      switchMap((action: EmployeeListActions.DeleteTeams) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loaderEl) => {
            return [loaderEl, action] as const;
          }),
        ),
      ),
      switchMap(([loaderEl, action]) =>
        this.plani8DataService.deleteTeams(action.deletePayload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loaderEl);
          }),
          map((res) => {
            this.employeeListingFacade.fetchEmployeeListInfo(action.fetchEmployeePayload);
            return new EmployeeListActions.GetTeamList();
          }),
          catchError(() => {
            return of(new EmployeeListActions.UpdateTeamsFailure());
          }),
        ),
      ),
    ),
  );
}
